import { supabase } from '../supabaseClient';
import { logAudit } from './auditLog';

// ============================================
// Deliverable Container CRUD
// ============================================

/**
 * 建立 Deliverable 容器（頁面）
 */
export const createDeliverable = async (data) => {
  try {
    const insertData = {
      phase_id: data.phase_id,
      company_id: data.company_id || null,
      title: data.title,
      sort_order: data.sort_order ?? 0,
    };

    const { data: deliverable, error } = await supabase
      .from('deliverables')
      .insert(insertData)
      .select('*, deliverable_items(*)')
      .single();

    if (error) throw error;

    await logAudit('create', 'deliverable', deliverable.id, deliverable.title, {
      after: deliverable,
    });

    return { data: deliverable, error: null };
  } catch (error) {
    console.error('Error creating deliverable:', error);
    return { data: null, error };
  }
};

/**
 * 更新 Deliverable 容器（標題、QA 驗證）
 */
export const updateDeliverable = async (id, updates) => {
  try {
    // 取得更新前資料
    const { data: beforeData } = await supabase
      .from('deliverables')
      .select('id, title, qa_status, qa_verified_by, qa_verified_at, sort_order, company_id, phase_id')
      .eq('id', id)
      .single();

    const updateData = {};
    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.qa_status !== undefined) updateData.qa_status = updates.qa_status;
    if (updates.qa_verified_by !== undefined) updateData.qa_verified_by = updates.qa_verified_by;
    if (updates.qa_verified_at !== undefined) updateData.qa_verified_at = updates.qa_verified_at;
    if (updates.sort_order !== undefined) updateData.sort_order = updates.sort_order;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.current_assignee_id !== undefined) updateData.current_assignee_id = updates.current_assignee_id;
    if (updates.handed_off_by !== undefined) updateData.handed_off_by = updates.handed_off_by;
    if (updates.handed_off_at !== undefined) updateData.handed_off_at = updates.handed_off_at;
    updateData.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('deliverables')
      .update(updateData)
      .eq('id', id)
      .select('*, deliverable_items(*)')
      .single();

    if (error) throw error;

    await logAudit('update', 'deliverable', id, data.title, {
      before: beforeData,
      after: updateData,
    });

    return { data, error: null };
  } catch (error) {
    console.error('Error updating deliverable:', error);
    return { data: null, error };
  }
};

/**
 * 刪除 Deliverable（CASCADE 刪除所有 items）
 */
export const deleteDeliverable = async (id) => {
  try {
    // 取得刪除前資料
    const { data: beforeData } = await supabase
      .from('deliverables')
      .select('id, title, phase_id, company_id')
      .eq('id', id)
      .single();

    const { error } = await supabase
      .from('deliverables')
      .delete()
      .eq('id', id);

    if (error) throw error;

    await logAudit('delete', 'deliverable', id, beforeData?.title, {
      before: beforeData,
    });

    return { error: null };
  } catch (error) {
    console.error('Error deleting deliverable:', error);
    return { error };
  }
};

// ============================================
// DeliverableItem CRUD
// ============================================

/**
 * 建立工作項目
 */
export const createDeliverableItem = async (data) => {
  try {
    const insertData = {
      deliverable_id: data.deliverable_id,
      company_id: data.company_id || null,
      role_type: data.role_type || 'Frontend',
      assignee_id: data.assignee_id || null,
      title: data.title,
      estimated_hours: data.estimated_hours ?? 0,
      actual_hours: data.actual_hours ?? 0,
      notes: data.notes || null,
      sort_order: data.sort_order ?? 0,
    };

    const { data: item, error } = await supabase
      .from('deliverable_items')
      .insert(insertData)
      .select()
      .single();

    if (error) throw error;

    await logAudit('create', 'deliverable_item', item.id, item.title, {
      after: item,
    });

    return { data: item, error: null };
  } catch (error) {
    console.error('Error creating deliverable item:', error);
    return { data: null, error };
  }
};

/**
 * 更新工作項目（is_completed 變 true 時自動帶 completed_at）
 */
export const updateDeliverableItem = async (id, updates) => {
  try {
    // 取得更新前資料
    const { data: beforeData } = await supabase
      .from('deliverable_items')
      .select('id, title, role_type, assignee_id, estimated_hours, actual_hours, notes, is_completed, company_id, deliverable_id')
      .eq('id', id)
      .single();

    const updateData = {};
    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.role_type !== undefined) updateData.role_type = updates.role_type;
    if (updates.assignee_id !== undefined) updateData.assignee_id = updates.assignee_id || null;
    if (updates.estimated_hours !== undefined) updateData.estimated_hours = updates.estimated_hours;
    if (updates.actual_hours !== undefined) updateData.actual_hours = updates.actual_hours;
    if (updates.notes !== undefined) updateData.notes = updates.notes;
    if (updates.sort_order !== undefined) updateData.sort_order = updates.sort_order;

    if (updates.is_completed !== undefined) {
      updateData.is_completed = updates.is_completed;
      updateData.completed_at = updates.is_completed ? new Date().toISOString() : null;
    }

    updateData.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('deliverable_items')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    await logAudit('update', 'deliverable_item', id, data.title, {
      before: beforeData,
      after: updateData,
    });

    return { data, error: null };
  } catch (error) {
    console.error('Error updating deliverable item:', error);
    return { data: null, error };
  }
};

/**
 * 刪除工作項目
 */
export const deleteDeliverableItem = async (id) => {
  try {
    // 取得刪除前資料
    const { data: beforeData } = await supabase
      .from('deliverable_items')
      .select('id, title, role_type, deliverable_id, company_id, assignee_id')
      .eq('id', id)
      .single();

    const { error } = await supabase
      .from('deliverable_items')
      .delete()
      .eq('id', id);

    if (error) throw error;

    await logAudit('delete', 'deliverable_item', id, beforeData?.title, {
      before: beforeData,
    });

    return { error: null };
  } catch (error) {
    console.error('Error deleting deliverable item:', error);
    return { error };
  }
};

/**
 * 批次更新同 deliverable 同角色的指派人
 */
export const batchUpdateItemAssignee = async (deliverableId, roleType, assigneeId) => {
  try {
    // 取得更新前資料
    const { data: beforeItems } = await supabase
      .from('deliverable_items')
      .select('id, title, assignee_id, company_id')
      .eq('deliverable_id', deliverableId)
      .eq('role_type', roleType);

    const { data, error } = await supabase
      .from('deliverable_items')
      .update({
        assignee_id: assigneeId || null,
        updated_at: new Date().toISOString(),
      })
      .eq('deliverable_id', deliverableId)
      .eq('role_type', roleType)
      .select();

    if (error) throw error;

    // 批次記錄每筆變更
    if (beforeItems?.length) {
      const { logAuditBatch } = await import('./auditLog');
      await logAuditBatch(
        beforeItems.map((item) => ({
          action: 'update',
          resourceType: 'deliverable_item',
          resourceId: item.id,
          resourceName: item.title,
          changes: {
            before: { assignee_id: item.assignee_id },
            after: { assignee_id: assigneeId || null },
          },
        }))
      );
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error batch updating assignee:', error);
    return { data: null, error };
  }
};

/**
 * 批次更新同 deliverable 同角色的角色名稱
 */
export const batchUpdateItemRoleType = async (deliverableId, oldRoleType, newRoleType) => {
  try {
    const { data, error } = await supabase
      .from('deliverable_items')
      .update({
        role_type: newRoleType,
        updated_at: new Date().toISOString(),
      })
      .eq('deliverable_id', deliverableId)
      .eq('role_type', oldRoleType)
      .select();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error batch updating role type:', error);
    return { data: null, error };
  }
};

// ============================================
// 交棒
// ============================================

/**
 * 交棒 Deliverable 給指定人員
 * 更新 current_assignee_id 並發送通知（in-app + email + Teams）
 *
 * @param {number} deliverableId
 * @param {string} targetUserId - 被交棒者的 user_id
 * @param {string} handedOffBy - 操作者的 user_id
 * @param {object} context - 額外資訊 { projectName, phaseName, deliverableTitle, targetEmail }
 */
export const handoffDeliverable = async (deliverableId, targetUserId, handedOffBy, context = {}) => {
  try {
    // 先查目前的 current_assignee_id，若相同則跳過通知
    const { data: current } = await supabase
      .from('deliverables')
      .select('current_assignee_id')
      .eq('id', deliverableId)
      .single();

    const isSamePerson = current?.current_assignee_id === targetUserId;
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('deliverables')
      .update({
        current_assignee_id: targetUserId,
        handed_off_by: handedOffBy,
        handed_off_at: now,
        updated_at: now,
      })
      .eq('id', deliverableId)
      .select('*, deliverable_items(*)')
      .single();

    if (error) throw error;

    await logAudit('update', 'deliverable', deliverableId, data.title, {
      before: { current_assignee_id: current?.current_assignee_id || null },
      after: { current_assignee_id: targetUserId, handed_off_by: handedOffBy },
    });

    // 通知被交棒者（不通知自己、不重複通知同一人）
    if (targetUserId !== handedOffBy && !isSamePerson) {
      const { createNotification, sendEmailNotification, buildEmailHtml, sendTeamsNotification } = await import('./notifications');
      const { isNotificationEnabled } = await import('./notificationPreferences');

      const label = [context.projectName, context.phaseName, context.deliverableTitle]
        .filter(Boolean)
        .join(' > ');

      const prefs = await isNotificationEnabled(targetUserId, 'deliverable_handoff');

      if (prefs.in_app) {
        createNotification({
          user_id: targetUserId,
          type: 'deliverable_handoff',
          title: '你收到一個交付項目交棒',
          message: label || data.title,
          resource_type: 'deliverable',
          resource_id: String(deliverableId),
          actor_id: handedOffBy,
        });
      }

      if (prefs.email && context.targetEmail) {
        const mainSystemUrl = import.meta.env.VITE_MAIN_SYSTEM_URL || '';
        sendEmailNotification({
          to: context.targetEmail,
          subject: `[Sonice] 交付項目交棒 — ${label || data.title}`,
          html: buildEmailHtml({
            title: '你收到一個交付項目交棒',
            message: label || data.title,
            ticketId: context.projectId || '',
            ticketLabel: label || data.title,
          }),
          type: 'deliverable_handoff',
        });
      }

      sendTeamsNotification({
        user_id: targetUserId,
        event_type: 'deliverable_handoff',
        title: '你收到一個交付項目交棒',
        message: label || data.title,
        ticketId: context.projectId || '',
        targetEmail: context.targetEmail || null,
      });
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error handing off deliverable:', error);
    return { data: null, error };
  }
};

// ============================================
// 查詢
// ============================================

/**
 * 查詢指派給某人的所有 DeliverableItems（含 deliverable → phase → project 資訊）
 * 供 MyDeliverables + DailyReport 使用
 */
/**
 * 查詢交棒給某人的所有 Deliverables（current_assignee_id = userId）
 * 供 MyDeliverables 使用，顯示「待處理交棒」
 */
export const getHandoffDeliverablesForUser = async (userId, { companyId } = {}) => {
  try {
    let query = supabase
      .from('deliverables')
      .select(`
        id, title, qa_status, current_assignee_id, handed_off_by, handed_off_at,
        phases:phase_id (
          id, name, project_id,
          projects:project_id (id, name, color)
        )
      `)
      .eq('current_assignee_id', userId)
      .order('handed_off_at', { ascending: false });

    if (companyId) {
      query = query.eq('company_id', companyId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching handoff deliverables for user:', error);
    return { data: [], error };
  }
};

export const getDeliverableItemsForUser = async (userId, { companyId } = {}) => {
  try {
    // 今天 00:00 (ISO)，用於篩選「今天才完成」的項目
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayISO = todayStart.toISOString();

    // 顯示：未完成 + 今天完成的（跟 ticket 邏輯一致）
    let query = supabase
      .from('deliverable_items')
      .select(`
        *,
        deliverables:deliverable_id (
          id, title, qa_status,
          phases:phase_id (
            id, name, project_id,
            projects:project_id (id, name, color)
          )
        )
      `)
      .eq('assignee_id', userId)
      .or(`is_completed.eq.false,completed_at.gte.${todayISO}`)
      .order('created_at', { ascending: true });

    if (companyId) {
      query = query.eq('company_id', companyId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching deliverable items for user:', error);
    return { data: [], error };
  }
};
